<template>
  <Teleport to="body">
    <div
      v-if="message !== null"
      class="chat-error-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      @click.self="close"
      @keydown.esc.prevent="close"
    >
      <div class="chat-error-modal__dialog">
        <div v-if="!fatal" class="chat-error-modal__controls">
          <button class="chat-error-modal__close" type="button" :aria-label="labels.close" @click="$emit('close')">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <div class="chat-error-modal__panel">
          <h2 :id="titleId">{{ labels.error_title }}</h2>
          <p>{{ message }}</p>
          <p v-if="productList" class="chat-error-modal__discard-notice">{{ labels.input_discarded }}</p>
          <p v-if="fatal">{{ labels.restart_operation }}</p>
          <button v-if="fatal" ref="actionButton" class="chat-error-modal__action" type="button" @click="$emit('chat-list')">{{ labels.chat_list }}</button>
          <button v-else-if="productList" ref="actionButton" class="chat-error-modal__action chat-error-modal__product-list" type="button" @click="$emit('product-list')">{{ labels.product_list }}</button>
          <button v-else ref="actionButton" class="chat-error-modal__action" type="button" @click="$emit('close')">{{ labels.close }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {nextTick, ref, watch} from 'vue';

const emit = defineEmits(['close', 'chat-list', 'product-list']);
const props = defineProps({
  message: {type: String, default: null},
  fatal: {type: Boolean, default: false},
  productList: {type: Boolean, default: false},
  labels: {type: Object, required: true},
});
const titleId = 'chat-error-modal-title';
const actionButton = ref(null);
let returnFocusElement = null;

const close = () => {
  if (!props.fatal) {
    emit('close');
  }
};

watch(() => props.message, async (message) => {
  if (message !== null) {
    returnFocusElement = document.activeElement;
    await nextTick();
    actionButton.value?.focus();
  } else if (returnFocusElement instanceof HTMLElement) {
    returnFocusElement.focus();
    returnFocusElement = null;
  }
});
</script>

<style scoped>
.chat-error-modal {
  position: fixed;
  z-index: 1050;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.45);
}

.chat-error-modal__panel {
  width: min(100%, 360px);
  padding: 24px;
  border-radius: 8px;
  background: #fff;
  text-align: center;
}

.chat-error-modal__dialog {
  display: flex;
  width: min(100%, 360px);
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.chat-error-modal__controls {
  display: flex;
  flex: none;
  justify-content: flex-end;
}

.chat-error-modal__close {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  font-size: 22px;
}

.chat-error-modal__panel h2 {
  margin: 0 0 16px;
  font-size: 20px;
}

.chat-error-modal__panel p {
  margin: 0 0 20px;
  white-space: pre-wrap;
}

.chat-error-modal__panel .chat-error-modal__discard-notice {
  margin-top: -12px;
  color: #999;
  font-size: 12px;
}

.chat-error-modal__panel button {
  min-width: 120px;
  padding: 10px 20px;
  border: 0;
  border-radius: 4px;
  color: #fff;
  background: #2dbb69;
}

.chat-error-modal__panel .chat-error-modal__product-list {
  background: #f28b22;
}
</style>
